import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
// TASK-66 PR-D: union (teams.player1/2 ∪ team_members.user_id) so
// captain users + UPPDRAG-60 customer_id-flow members are resolved
// via the team_members branch (was UPPDRAG-62 PR-D's three-source
// union; captain_id stripped).
import { fetchTeamsForUser } from "@/lib/teamMembershipQueries";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LeagueList from "@/components/leagues/LeagueList";
import { Card, CardContent } from "@/components/ui/card";
import { Trophy } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import MasterDetailLayout from "@/components/layout/MasterDetailLayout";
import LeagueListPanel from "@/components/leagues/LeagueListPanel";
import LeagueDetailPanel from "@/components/leagues/LeagueDetailPanel";
import PlayerLeagueCard from "@/components/leagues/PlayerLeagueCard";

export default function Leagues() {
  const { user } = useAuth();
  const { t } = useTranslation("common");
  const [selectedLeagueId, setSelectedLeagueId] = useState<string | null>(null);

  // Fetch leagues where the user is a team member (for mobile view).
  // PR-16.2: extended to include the sponsor URL columns + halls.name +
  // play_days + registration_deadline so the shared PlayerLeagueCard
  // has every field it needs.
  // TASK-66 PR-D: fetchTeamsForUser unions teams.player1/2 +
  // team_members.user_id, deduped. Captain users still resolve via the
  // team_members branch.
  const { data: myTeams, isLoading: teamsLoading } = useQuery({
    queryKey: ["my-league-teams", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      return await fetchTeamsForUser<Record<string, any>>(supabase, user.id, {
        status: "active",
        columns: `
          *,
          leagues:league_id (
            id, name, category, status, start_date, end_date,
            registration_deadline, play_days,
            sponsor_my_series_logo_url, sponsor_my_series_url,
            sponsor_register_logo_url, sponsor_register_url,
            halls(name)
          ),
          divisions:division_id (id, name, level)
        `,
      });
    },
    enabled: !!user,
  });

  const activeTeams = (myTeams ?? []).filter(
    (t: any) => t.leagues?.status === "active" || t.leagues?.status === "registration_open",
  );

  // PR-16.2: derive the active round + scheduled matches per league, in a
  // single batched query. The PlayerLeagueCard "my_series" variant uses
  // these to compute weekdays from actually-scheduled matches (Q2 spec).
  // Falls back to league.play_days if no active round or no scheduled matches.
  const activeLeagueIds = activeTeams
    .map((t: any) => t.leagues?.id as string | undefined)
    .filter((id): id is string => !!id);

  const { data: activeRoundsByLeague = {} } = useQuery<
    Record<string, { activeRoundNumber: number | null; matches: { round: number; scheduled_date: string | null }[] }>
  >({
    queryKey: ["my-league-active-rounds", activeLeagueIds.sort().join(",")],
    queryFn: async () => {
      if (activeLeagueIds.length === 0) return {};
      // Step 1: active round per league
      const { data: rounds } = await supabase
        .from("league_rounds" as never)
        .select("league_id, round_number, status")
        .in("league_id", activeLeagueIds)
        .eq("status", "active");
      const activeByLeague = new Map<string, number>();
      for (const r of (rounds ?? []) as unknown as { league_id: string; round_number: number }[]) {
        activeByLeague.set(r.league_id, r.round_number);
      }
      // Step 2: matches across all of those leagues (we'll filter per
      // league_id+round in JS — bounded set, O(active leagues × matches/round))
      const { data: matches } = await supabase
        .from("league_matches")
        .select("league_id, round, scheduled_date")
        .in("league_id", activeLeagueIds);
      const out: Record<
        string,
        { activeRoundNumber: number | null; matches: { round: number; scheduled_date: string | null }[] }
      > = {};
      for (const id of activeLeagueIds) {
        out[id] = {
          activeRoundNumber: activeByLeague.get(id) ?? null,
          matches: (matches ?? []).filter((m) => m.league_id === id).map((m) => ({
            round: m.round,
            scheduled_date: m.scheduled_date as string | null,
          })),
        };
      }
      return out;
    },
    enabled: activeLeagueIds.length > 0,
  });

  // Mobile view content
  const mobileContent = (
    <div className="space-y-6 pb-20">
      <div className="gradient-hero rounded-2xl p-5 -mx-1 space-y-1">
        <div className="flex items-center gap-2">
          <Trophy className="h-6 w-6 text-accent" />
          <h1 className="text-2xl font-bold">{t("nav.leagues")}</h1>
        </div>
        <p className="text-muted-foreground">{t("leaguesPage.subtitle")}</p>
        {/* G-035/038: wayfinding to the per-player match list */}
        <Link to="/leagues/mine" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
          {t("myLeagueMatches")} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <Tabs defaultValue="my" className="w-full">
        <TabsList className="w-full">
          <TabsTrigger value="my" className="flex-1">{t("leaguesPage.tabs.mine")}</TabsTrigger>
          <TabsTrigger value="open" className="flex-1">{t("leaguesPage.tabs.register")}</TabsTrigger>
          <TabsTrigger value="archive" className="flex-1">{t("leaguesPage.tabs.archive")}</TabsTrigger>
        </TabsList>

        <TabsContent value="my" className="mt-4 space-y-3">
          {teamsLoading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => <Skeleton key={i} className="h-24 w-full rounded-lg" />)}
            </div>
          ) : activeTeams && activeTeams.length > 0 ? (
            // PR-16.2: shared <PlayerLeagueCard> variant="my_series".
            // Active-round weekdays derive from scheduled matches via
            // activeRoundsByLeague[leagueId]; falls back to play_days
            // when no active round / no scheduled matches.
            activeTeams.map((team: any) => {
              const league = team.leagues;
              if (!league) return null;
              const roundData = activeRoundsByLeague[league.id];
              return (
                <PlayerLeagueCard
                  key={team.id}
                  league={league}
                  variant="my_series"
                  activeRoundNumber={roundData?.activeRoundNumber ?? null}
                  matches={roundData?.matches ?? []}
                />
              );
            })
          ) : (
            <Card className="border-dashed">
              <CardContent className="p-6 text-center text-muted-foreground gradient-hero rounded-lg">
                <Trophy className="h-8 w-8 mx-auto mb-2 text-accent/60" />
                <p>{t("leaguesPage.notRegistered")}</p>
                <p className="text-sm mt-1">{t("leaguesPage.checkRegisterTab")}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="open" className="mt-4">
          <LeagueList status="registration_open" userId={user?.id} />
        </TabsContent>

        <TabsContent value="archive" className="mt-4">
          <LeagueList status="completed" userId={user?.id} />
        </TabsContent>
      </Tabs>
    </div>
  );

  return (
    <>
      {/* Mobile: original tabbed view */}
      <div className="lg:hidden">
        {mobileContent}
      </div>

      {/* Desktop: master-detail layout */}
      <div className="hidden lg:block h-[calc(100vh-8rem)]">
        <div className="flex justify-end pb-2">
          <Link to="/leagues/mine" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
            {t("myLeagueMatches")} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <MasterDetailLayout
          list={
            <LeagueListPanel
              selectedLeagueId={selectedLeagueId}
              onSelectLeague={setSelectedLeagueId}
            />
          }
          detail={
            selectedLeagueId ? (
              <LeagueDetailPanel leagueId={selectedLeagueId} />
            ) : null
          }
          listWidth="lg:w-72"
          emptyDetail={
            <div className="flex flex-1 items-center justify-center text-muted-foreground">
              <div className="text-center space-y-2">
                <Trophy className="h-8 w-8 mx-auto opacity-50" />
                <p>{t("leaguesPage.selectLeague")}</p>
              </div>
            </div>
          }
        />
      </div>
    </>
  );
}
